<template>
  <span
    ref="ownerRef"
    class="help-hint"
    :class="{ 'help-hint--custom': hasCustomTrigger }"
  >
    <slot v-if="hasCustomTrigger" :ariaDescribedby="hintId" />
    <button
      v-else
      ref="defaultTriggerRef"
      v-bind="triggerProps"
      type="button"
      :class="['help-hint__trigger', `help-hint__trigger--${size}`]"
    >
      {{ icon }}
    </button>

    <Teleport to="body">
      <Transition name="help-hint-fade">
        <span
          v-show="visible"
          :id="hintId"
          ref="hintRef"
          class="help-hint__content"
          role="tooltip"
          :style="positionStyle"
        >
          {{ message }}
        </span>
      </Transition>
    </Teleport>
  </span>
</template>

<script setup>
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  useSlots,
  watch,
} from "vue";

let helpHintInstanceCount = 0;

const props = defineProps({
  message: {
    type: String,
    required: true,
  },
  icon: {
    type: String,
    default: "i",
  },
  size: {
    type: String,
    default: "normal",
    validator: (value) =>
      ["small", "normal", "medium", "large"].includes(value),
  },
  position: {
    type: String,
    default: "top",
    validator: (value) => ["top", "bottom", "left", "right"].includes(value),
  },
  delay: {
    type: Number,
    default: 300,
  },
  triggerLabel: {
    type: String,
    default: "",
  },
});

const slots = useSlots();
const hasCustomTrigger = computed(() => Boolean(slots.default));
const visible = ref(false);
const ownerRef = ref(null);
const defaultTriggerRef = ref(null);
const hintRef = ref(null);
const positionStyle = ref({ top: "0px", left: "0px" });
const hintId = `help-hint-${++helpHintInstanceCount}`;
let showTimer = null;

const clearShowTimer = () => {
  if (!showTimer) return;
  window.clearTimeout(showTimer);
  showTimer = null;
};

const calculatePosition = () => {
  const owner = ownerRef.value;
  const hint = hintRef.value;
  if (!owner || !hint || typeof window === "undefined") return;

  const ownerRect = owner.getBoundingClientRect();
  const hintRect = hint.getBoundingClientRect();
  const gap = 8;
  const viewportPadding = 8;
  let preferredPosition = props.position;

  if (
    preferredPosition === "top" &&
    ownerRect.top - hintRect.height - gap < viewportPadding
  ) {
    preferredPosition = "bottom";
  } else if (
    preferredPosition === "bottom" &&
    ownerRect.bottom + hintRect.height + gap > window.innerHeight - viewportPadding
  ) {
    preferredPosition = "top";
  }

  let top = ownerRect.top + ownerRect.height / 2 - hintRect.height / 2;
  let left = ownerRect.left + ownerRect.width / 2 - hintRect.width / 2;

  if (preferredPosition === "top") top = ownerRect.top - hintRect.height - gap;
  if (preferredPosition === "bottom") top = ownerRect.bottom + gap;
  if (preferredPosition === "left") left = ownerRect.left - hintRect.width - gap;
  if (preferredPosition === "right") left = ownerRect.right + gap;

  const maxLeft = Math.max(
    viewportPadding,
    window.innerWidth - hintRect.width - viewportPadding,
  );
  const maxTop = Math.max(
    viewportPadding,
    window.innerHeight - hintRect.height - viewportPadding,
  );

  positionStyle.value = {
    top: `${Math.min(Math.max(top, viewportPadding), maxTop)}px`,
    left: `${Math.min(Math.max(left, viewportPadding), maxLeft)}px`,
  };
};

const show = async ({ delayed = false } = {}) => {
  clearShowTimer();
  const reveal = async () => {
    visible.value = true;
    await nextTick();
    calculatePosition();
  };

  if (delayed && props.delay > 0) {
    showTimer = window.setTimeout(reveal, props.delay);
    return;
  }
  await reveal();
};

const hide = () => {
  clearShowTimer();
  visible.value = false;
};

const toggle = () => {
  if (visible.value) hide();
  else show();
};

const handleKeydown = (event) => {
  if (event.key !== "Escape") return;
  hide();
  event.stopPropagation();
};

const handleOwnerPointerEnter = () => show({ delayed: true });
const handleOwnerPointerLeave = () => hide();
const handleOwnerFocusIn = () => show();
const handleOwnerFocusOut = (event) => {
  if (ownerRef.value?.contains(event.relatedTarget)) return;
  hide();
};
const handleOwnerClick = (event) => {
  if (event.detail === 0) return;
  show();
};
const handleOwnerKeydown = (event) => {
  if (event.key === "Escape") handleKeydown(event);
};

const triggerProps = computed(() => ({
  "aria-describedby": hintId,
  "aria-label": props.triggerLabel || (props.icon === "i" ? "More information" : props.icon),
  onMouseenter: () => show({ delayed: true }),
  onMouseleave: hide,
  onFocus: () => show(),
  onBlur: hide,
  onClick: toggle,
  onKeydown: handleKeydown,
}));

const handleOutsidePointer = (event) => {
  if (!visible.value || ownerRef.value?.contains(event.target)) return;
  hide();
};

onMounted(() => {
  if (hasCustomTrigger.value && ownerRef.value) {
    ownerRef.value.addEventListener("mouseenter", handleOwnerPointerEnter);
    ownerRef.value.addEventListener("mouseleave", handleOwnerPointerLeave);
    ownerRef.value.addEventListener("focusin", handleOwnerFocusIn);
    ownerRef.value.addEventListener("focusout", handleOwnerFocusOut);
    ownerRef.value.addEventListener("click", handleOwnerClick);
    ownerRef.value.addEventListener("keydown", handleOwnerKeydown);
  }
  document.addEventListener("pointerdown", handleOutsidePointer);
  window.addEventListener("resize", calculatePosition);
  window.addEventListener("scroll", calculatePosition, true);
});

onBeforeUnmount(() => {
  clearShowTimer();
  if (ownerRef.value) {
    ownerRef.value.removeEventListener("mouseenter", handleOwnerPointerEnter);
    ownerRef.value.removeEventListener("mouseleave", handleOwnerPointerLeave);
    ownerRef.value.removeEventListener("focusin", handleOwnerFocusIn);
    ownerRef.value.removeEventListener("focusout", handleOwnerFocusOut);
    ownerRef.value.removeEventListener("click", handleOwnerClick);
    ownerRef.value.removeEventListener("keydown", handleOwnerKeydown);
  }
  document.removeEventListener("pointerdown", handleOutsidePointer);
  window.removeEventListener("resize", calculatePosition);
  window.removeEventListener("scroll", calculatePosition, true);
});

watch(() => props.message, () => {
  if (visible.value) nextTick(calculatePosition);
});
</script>

<style scoped>
.help-hint {
  display: inline-flex;
  align-items: center;
  margin-inline-start: var(--space-2);
}

.help-hint--custom {
  margin-inline-start: 0;
}

.help-hint__trigger {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: var(--control-height-micro);
  height: var(--control-height-micro);
  padding: 0 var(--space-1);
  border: 1px solid var(--color-border-strong);
  border-radius: var(--radius-pill);
  background: transparent;
  color: var(--color-text-primary);
  font: inherit;
  font-size: var(--font-size-label);
  line-height: 1;
  cursor: help;
  transition: transform var(--motion-standard) var(--motion-easing-standard);
}

.help-hint__trigger--large {
  min-width: var(--control-height-default);
  height: var(--control-height-default);
  padding-inline: var(--space-5);
  font-size: var(--font-size-body);
}

.help-hint__trigger--medium {
  min-width: var(--control-height-dense);
  height: var(--control-height-dense);
}

.help-hint__trigger:hover,
.help-hint__trigger:active {
  transform: translateY(var(--motion-pressed-offset));
}

.help-hint__content {
  position: fixed;
  z-index: 9999;
  display: block;
  width: max-content;
  max-width: min(280px, calc(100vw - 16px));
  padding: var(--space-4) var(--space-5);
  border-radius: var(--radius-sm);
  background: var(--color-surface-inverse);
  box-shadow: var(--shadow-surface-floating);
  color: var(--color-text-on-media);
  font-size: var(--font-size-caption);
  font-weight: var(--font-weight-medium);
  line-height: 1.4;
  overflow-wrap: anywhere;
  pointer-events: none;
}

.help-hint-fade-enter-active,
.help-hint-fade-leave-active {
  transition: opacity var(--motion-standard) var(--motion-easing-standard);
}

.help-hint-fade-enter-from,
.help-hint-fade-leave-to {
  opacity: 0;
}
</style>
